import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from 'recharts';

/**
 * Strengths Analysis Component
 * Shows quiz performance and lesson completion strengths
 */
export function StrengthsAnalysis({ strengthsData }) {
  const { quizAttempts, lessonProgress } = strengthsData || {};

  const analysisData = useMemo(() => {
    if (!quizAttempts || !lessonProgress) {
      return { quizStats: [], sectionStrengths: [], topStrengths: [] };
    }

    // Quiz performance by lesson section
    const sectionQuizMap = {};
    quizAttempts?.forEach(quiz => {
      const section = quiz?.lessonSection || 'unknown';
      if (!sectionQuizMap?.[section]) {
        sectionQuizMap[section] = {
          section,
          totalScore: 0,
          attempts: 0,
          perfectCount: 0
        };
      }
      sectionQuizMap[section].totalScore += quiz?.score;
      sectionQuizMap[section].attempts++;
      if (quiz?.isPerfect) {
        sectionQuizMap[section].perfectCount++;
      }
    });

    const quizStats = Object.values(sectionQuizMap)?.map(stat => ({
      section: stat?.section,
      avgScore: Math.round(stat?.totalScore / stat?.attempts),
      perfectRate: Math.round((stat?.perfectCount / stat?.attempts) * 100),
      attempts: stat?.attempts
    }));

    // Section strengths from lesson progress
    const sectionProgressMap = {};
    lessonProgress?.forEach(lesson => {
      const section = lesson?.lessonSection || 'unknown';
      if (!sectionProgressMap?.[section]) {
        sectionProgressMap[section] = {
          section,
          completed: 0,
          total: 0,
          avgProgress: 0
        };
      }
      sectionProgressMap[section].total++;
      sectionProgressMap[section].avgProgress += lesson?.videoProgressPercentage;
      if (lesson?.lessonStatus === 'completed') {
        sectionProgressMap[section].completed++;
      }
    });

    const sectionStrengths = Object.values(sectionProgressMap)?.map(stat => ({
      section: stat?.section?.replace(/_/g, ' ')?.toUpperCase(),
      completionRate: Math.round((stat?.completed / stat?.total) * 100),
      avgProgress: Math.round(stat?.avgProgress / stat?.total)
    }));

    // Identify top strengths (sections with >80% completion and >80 avg quiz score)
    const topStrengths = sectionStrengths?.map(section => {
        const quizStat = quizStats?.find(q => q?.section === section?.section?.toLowerCase()?.replace(/ /g, '_'));
        return {
          ...section,
          quizScore: quizStat?.avgScore || 0
        };
      })?.filter(section => section?.completionRate >= 80 && section?.quizScore >= 80)?.sort((a, b) => (b?.completionRate + b?.quizScore) - (a?.completionRate + a?.quizScore))?.slice(0, 3);

    return { quizStats, sectionStrengths, topStrengths };
  }, [quizAttempts, lessonProgress]);

  if (!quizAttempts || quizAttempts?.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        No quiz data available yet. Complete quizzes to see your strengths!
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Strengths */}
      {analysisData?.topStrengths?.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold mb-3 text-white">Your Top Strengths 🌟</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {analysisData?.topStrengths?.map((strength, index) => (
              <div 
                key={strength?.section}
                className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-xl p-4 border border-purple-400/30"
              >
                <div className="text-2xl mb-1">
                  {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                </div>
                <div className="text-sm font-semibold text-white capitalize">{strength?.section}</div>
                <div className="text-xs text-gray-300 mt-2">
                  <div>{strength?.completionRate}% completed</div>
                  <div>{strength?.quizScore}% avg quiz score</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Quiz Performance Radar */}
      <div>
        <h4 className="text-lg font-semibold mb-4 text-white">Quiz Performance by Section</h4>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={analysisData?.sectionStrengths}>
            <PolarGrid stroke="rgba(255,255,255,0.2)" />
            <PolarAngleAxis 
              dataKey="section" 
              stroke="rgba(255,255,255,0.6)"
              style={{ fontSize: '11px' }}
            />
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, 100]}
              stroke="rgba(255,255,255,0.4)"
            />
            <Radar 
              name="Completion Rate" 
              dataKey="completionRate" 
              stroke="#00D4FF" 
              fill="#00D4FF" 
              fillOpacity={0.5}
            />
            <Radar 
              name="Avg Progress" 
              dataKey="avgProgress" 
              stroke="#FFD700" 
              fill="#FFD700" 
              fillOpacity={0.5}
            />
            <Legend />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(50, 33, 93, 0.95)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                color: '#fff'
              }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      {/* Quiz Score Distribution */}
      <div>
        <h4 className="text-lg font-semibold mb-4 text-white">Average Quiz Scores</h4>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={analysisData?.quizStats}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis 
              dataKey="section" 
              stroke="rgba(255,255,255,0.6)"
              style={{ fontSize: '11px' }}
            />
            <YAxis 
              stroke="rgba(255,255,255,0.6)"
              domain={[0, 100]}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(50, 33, 93, 0.95)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                color: '#fff'
              }}
            />
            <Bar dataKey="avgScore" fill="#9D4EDD" name="Avg Score" radius={[8, 8, 0, 0]} />
            <Bar dataKey="perfectRate" fill="#06FFA5" name="Perfect %" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}